import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, FileText, Pill, TestTube, Stethoscope, Scan, FileHeart } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const CATEGORY_META = {
  prescription: { icon: Pill, label: "Prescription", color: "text-[#854D0E]" },
  lab: { icon: TestTube, label: "Lab", color: "text-[#334155]" },
  diagnosis: { icon: Stethoscope, label: "Diagnosis", color: "text-primary" },
  imaging: { icon: Scan, label: "Imaging", color: "text-accent" },
  other: { icon: FileText, label: "Other", color: "text-muted-foreground" },
};

export default function PatientRecords() {
  const [records, setRecords] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "other", description: "", provider_name: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/patient/records");
    setRecords(data);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/patient/records", form);
      toast.success("Record added");
      setOpen(false);
      setForm({ title: "", category: "other", description: "", provider_name: "" });
      load();
    } catch (e) {
      toast.error("Could not save record");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell
      pageTitle="Medical records"
      pageDescription="Your personal, secure health file — prescriptions, labs, diagnoses, and imaging."
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-md" data-testid="add-record-button"><Plus size={14} className="mr-1.5" /> Add record</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Add medical record</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input id="title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g., Blood test — CBC" data-testid="record-title-input" />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger id="category" data-testid="record-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_META).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="desc">Notes</Label>
                <Textarea id="desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="record-description-input" />
              </div>
              <div>
                <Label htmlFor="prov">Provider</Label>
                <Input id="prov" value={form.provider_name} onChange={(e) => setForm({ ...form, provider_name: e.target.value })} placeholder="e.g., Apollo Diagnostics" data-testid="record-provider-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving || !form.title} data-testid="record-save-button">
                {saving ? "Saving…" : "Save record"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      {records.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center">
          <FileHeart size={28} className="mx-auto text-muted-foreground" />
          <div className="mt-3 font-serif text-xl text-primary">No records yet</div>
          <p className="text-sm text-muted-foreground mt-1">Start building your health file. Add your first record.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="records-list">
          {records.map((r) => {
            const meta = CATEGORY_META[r.category] || CATEGORY_META.other;
            const Icon = meta.icon;
            return (
              <div key={r.id} className="rounded-lg border border-border bg-white p-5" data-testid={`record-card-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wider ${meta.color}`}>
                    <Icon size={14} /> {meta.label}
                  </div>
                  <span className="text-[10px] text-muted-foreground">{formatDate(r.created_at)}</span>
                </div>
                <h3 className="mt-3 font-serif text-lg text-primary">{r.title}</h3>
                {r.description && <p className="mt-1 text-sm text-muted-foreground">{r.description}</p>}
                {r.provider_name && <div className="mt-3 text-xs text-muted-foreground">Provider: {r.provider_name}</div>}
              </div>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-IN", { dateStyle: "medium" }); }
  catch { return iso; }
}
